import {
  BufferAttribute,
  CatmullRomCurve3,
  Group,
  IcosahedronGeometry,
  Matrix4,
  Vector3,
  TubeGeometry,
  CylinderGeometry,
  Quaternion,
  DoubleSide,
  BatchedMesh,
  Color,
  ShaderMaterial,
  Float32BufferAttribute,
} from 'three'
import type {
  DrillDataConfig,
  DrillHoleApiData,
  DrillHoles,
  DrillLegendViewModel,
  NumericalLegend,
  CategoricalLegend,
} from '~/entities/drillHole'
import type { ApiListView } from '~/entities/api'
import type { BufferGeometry } from 'three'
import { disposeScene } from '~/lib/three-d-viewer/utils'
import assayShader from '~/lib/three-d-resources/shaders/drill-holes/assay-shader'
import collarShader from './shaders/drill-holes/collar-shader'
import surveyShader from './shaders/drill-holes/survey-shader'

const lightPos1 = 10_000_000
const lightPos2 = -10_100_000
const uLightPositions = [new Vector3(lightPos1 / 3, lightPos1, lightPos1 / 3), new Vector3(lightPos2 / 3, lightPos2, lightPos2 / 3)]

export class DrillHoleManager {
  public group = new Group()

  private loaderConfig: { uuid: string | null }
  private surveyCurves: Map<string, CatmullRomCurve3> = new Map()
  private assayMap: Map<number, BatchedMesh> = new Map()
  private collarMap: Map<number, BatchedMesh> = new Map()
  private surveyMap: Map<number, BatchedMesh> = new Map()

  constructor(loaderConfig: { uuid: string | null }) {
    this.loaderConfig = loaderConfig
    this.group.name = 'Drill Holes'
  }

  getSelectedLegendForDataSet(
    dataSetId: number,
    registeredLegends: Map<string | number, DrillLegendViewModel>,
  ): DrillLegendViewModel | undefined {
    const legend = Array.from(registeredLegends.values()).find(legend => legend.dataSetId === dataSetId && legend.isSelected)
    return legend
  }

  private _createAllCollars(
    dataSetId: number,
    drillHoles: DrillHoleApiData[],
    localizationOffset: Vector3,
    count: number,
    config: DrillDataConfig,
    appendPage = false,
  ) {
    let batchedMesh: BatchedMesh
    if (appendPage) {
      batchedMesh = this.collarMap.get(dataSetId)!
    } else {
      const radius = config.collar?.radius ?? 3
      const color = config.collar?.color ?? 0xe6e6e6
      const maxInstanceCount = count
      const vertexCountPerGeometry = 60 // dodecahedron geometry
      const maxVertexCount = vertexCountPerGeometry * maxInstanceCount
      const maxIndexCount = 20 * maxInstanceCount // increase the multiplier if we encounter index buffer overflow

      const shaderMaterial = new ShaderMaterial({ ...collarShader })
      shaderMaterial.uniforms.uColor.value = new Color(color)
      shaderMaterial.uniforms.uRadius.value = radius
      shaderMaterial.uniforms.uLightPositions.value = uLightPositions
      batchedMesh = new BatchedMesh(maxInstanceCount, maxVertexCount, maxIndexCount, shaderMaterial)
    }

    batchedMesh.visible = config.collar?.visible ?? true

    const matrix = new Matrix4()
    for (let drillHole of drillHoles) {
      const collar = drillHole.collar
      const collarGeo = new IcosahedronGeometry(1)

      const position = new Vector3(collar?.point.x, collar?.point.y, collar?.point.z).sub(localizationOffset)
      const vertexCount = collarGeo.attributes.position.count

      const origin = new Float32Array(vertexCount * 3)
      for (let i = 0; i < vertexCount; i++) {
        origin.set([position.x, position.y, position.z], i * 3)
      }

      collarGeo.setAttribute('origin', new BufferAttribute(origin, 3))

      matrix.setPosition(position)
      collarGeo.applyMatrix4(matrix)

      try {
        const collarGeometryId = batchedMesh.addGeometry(collarGeo)
        batchedMesh.addInstance(collarGeometryId)
      } catch (e) {
        console.error(e)
      }
    }

    batchedMesh.computeBoundingSphere()
    this.group.add(batchedMesh)
    this.collarMap.set(dataSetId, batchedMesh)
  }

  private _createAllSurveys(
    dataSetId: number,
    drillHoles: DrillHoleApiData[],
    localizationOffset: Vector3,
    count: number,
    config: DrillDataConfig,
    appendPage = false,
  ) {
    let batchedMesh: BatchedMesh
    if (appendPage) {
      batchedMesh = this.surveyMap.get(dataSetId)!
    } else {
      const radius = config.survey?.radius ?? 1
      const color = config.survey?.color ?? 0xe6e6e6
      const maxInstanceCount = count
      const maxTubeSegments = Math.max(...drillHoles.map(hole => hole.survey?.points.length ?? 0));
      const radialSegments = 5;
      const maxVertexCountPerGeometry = (maxTubeSegments + 1) * (radialSegments + 1) // tube geometry vertices
      const maxVertexCount = maxVertexCountPerGeometry * maxInstanceCount
      const maxIndexCountPerGeometry = radialSegments * maxTubeSegments * 6; // tube geometry indexes
      const maxIndexCount = maxIndexCountPerGeometry * maxInstanceCount

      const shaderMaterial = new ShaderMaterial({ ...surveyShader, side: DoubleSide })
      shaderMaterial.uniforms.uColor.value = new Color(color)
      shaderMaterial.uniforms.uRadius.value = radius
      shaderMaterial.uniforms.uLightPositions.value = uLightPositions
      batchedMesh = new BatchedMesh(maxInstanceCount, maxVertexCount, maxIndexCount, shaderMaterial)
    }

    batchedMesh.visible = config.survey?.visible ?? true

    for (let drillHole of drillHoles) {
      if (!drillHole.survey?.points) continue
      const points: Vector3[] = []

      for (let j = 0; j < drillHole.survey.points.length; j++) {
        const survey = drillHole.survey
        const pos = new Vector3(survey.points[j].x, survey.points[j].y, survey.points[j].z).sub(localizationOffset)
        points.push(pos)
      }

      const curve = new CatmullRomCurve3(points)
      this.surveyCurves.set(drillHole.hole_id, curve)
      const geo = new TubeGeometry(curve, drillHole.survey.points.length - 1, 1, 5, false)

      try {
        const surveyGeometryId = batchedMesh.addGeometry(geo)
        batchedMesh.addInstance(surveyGeometryId)
      } catch (e) {
        console.error(e)
      }
    }
    batchedMesh.computeBoundingSphere()
    this.group.add(batchedMesh)
    this.surveyMap.set(dataSetId, batchedMesh)
  }

  _adjustCylinderCapNormals(geometry: CylinderGeometry, height: number) {
    const position = geometry.attributes.position
    const normals = geometry.attributes.normal
    const vertexCount = position.count
    const tempVec = new Vector3()

    for (let i = 0; i < vertexCount; i++) {
      tempVec.set(position.getX(i), position.getY(i), position.getZ(i))
      if (
        Math.abs(tempVec.y - height / 2) < 1e-6 || // Top cap
        Math.abs(tempVec.y + height / 2) < 1e-6 // Bottom cap
      ) {
        tempVec.setY(0).normalize() // point radially outward
        normals.setXYZ(i, tempVec.x, tempVec.y, tempVec.z)
      }
    }
    normals.needsUpdate = true
  }

  private _createAllNumericalAssays(
    dataSetId: number,
    holes: DrillHoleApiData[],
    resourceName: string | undefined,
    config: DrillDataConfig,
    maxFromTo: number,
    count: number,
    appendPage = false,
  ) {
    if (!resourceName) return
    const resourceIndex = holes[0].numerical_assay?.resource_names?.indexOf(resourceName)
    if (resourceIndex === undefined || resourceIndex === -1) return

    const numericalAssay = (config.numerical_assay as NumericalLegend[])?.find(c => c.original.name === resourceName)
    if (!numericalAssay) return

    let batchedMesh: BatchedMesh
    if (appendPage) {
      batchedMesh = this.assayMap.get(dataSetId)!
    } else {
      batchedMesh = this._createAssayBatchedMesh(count, maxFromTo)
    }

    const grades = numericalAssay.grades
    const uColors = []
    for (let i = 0; i < 15; i++) {
      uColors.push(new Color(grades[i]?.color ?? 0xffffff))
    }
    ;(batchedMesh.material as ShaderMaterial).uniforms.uColors.value = uColors
    ;(batchedMesh.material as ShaderMaterial).uniforms.uRadius.value = config.highlight?.radius ?? 4
    batchedMesh.visible = config.highlight?.visible ?? true

    for (let i = 0; i < holes.length; i++) {
      const from_to = holes[i].numerical_assay.from_to
      const resources = holes[i].numerical_assay.resources
      const curve = this.surveyCurves.get(holes[i].hole_id)
      if (!(from_to && resources && curve)) continue
      const depth = holes[i].collar.depth
      for (let j = 0; j < from_to.from.length; j++) {
        const resource = resources[j][resourceIndex] // second index references resource type

        const geo = this._createAssayGeometryAtDepth(from_to.from[j], from_to.to[j], depth, curve)
        if (!geo) continue

        const gradeIndex = this._getGradeIndex(resource, grades)
        const colorIndex = []
        for (let i = 0; i < geo.attributes.position.count; i++) {
          colorIndex.push(gradeIndex)
        }
        geo.setAttribute('colorIndex', new Float32BufferAttribute(colorIndex, 1))

        try {
          const assayGeometryId = batchedMesh.addGeometry(geo)
          batchedMesh.addInstance(assayGeometryId)
        } catch (e) {
          console.error(e)
        }
      }
    }

    batchedMesh.computeBoundingSphere()
    this.group.add(batchedMesh)
    this.assayMap.set(dataSetId, batchedMesh)
  }

  private _createAllCategoricalAssays(
    dataSetId: number,
    holes: DrillHoleApiData[],
    config: DrillDataConfig,
    maxFromTo: number,
    count: number,
    appendPage = false,
  ) {
    if (!config.categorical_assay) return
    const categoricalAssay = config.categorical_assay[0] as CategoricalLegend

    let batchedMesh: BatchedMesh
    if (appendPage) {
      batchedMesh = this.assayMap.get(dataSetId)!
    } else {
      batchedMesh = this._createAssayBatchedMesh(count, maxFromTo)
    }

    const categories = categoricalAssay.categories
    const uColors = []
    for (let i = 0; i < 15; i++) {
      uColors.push(new Color(categories[i]?.color ?? 0xffffff))
    }
    ;(batchedMesh.material as ShaderMaterial).uniforms.uColors.value = uColors
    ;(batchedMesh.material as ShaderMaterial).uniforms.uRadius.value = config.highlight?.radius ?? 4
    batchedMesh.visible = config.highlight?.visible ?? true

    for (let i = 0; i < holes.length; i++) {
      const from_to = holes[i].categorical_assay.from_to
      const resources = holes[i].categorical_assay.resources
      const curve = this.surveyCurves.get(holes[i].hole_id)
      if (!(from_to && resources && curve)) continue
      const depth = holes[i].collar.depth
      for (let j = 0; j < from_to.from.length; j++) {
        const resource = resources[j]

        const geo = this._createAssayGeometryAtDepth(from_to.from[j], from_to.to[j], depth, curve)
        if (!geo) continue

        const categoryIndex = categories.findIndex((category: any) => category.name === resource)
        const colorIndex = []
        for (let i = 0; i < geo.attributes.position.count; i++) {
          colorIndex.push(categoryIndex)
        }
        geo.setAttribute('colorIndex', new Float32BufferAttribute(colorIndex, 1))

        try {
          const assayGeometryId = batchedMesh.addGeometry(geo)
          batchedMesh.addInstance(assayGeometryId)
        } catch (e) {
          console.error(e)
        }
      }
    }

    batchedMesh.computeBoundingSphere()
    this.group.add(batchedMesh)
    this.assayMap.set(dataSetId, batchedMesh)
  }

  _createAssayGeometryAtDepth(from: number, to: number, depth: number, curve: CatmullRomCurve3): BufferGeometry | undefined {
    const height = to - from
    const geo = new CylinderGeometry(1, 1, height, 5)
    this._adjustCylinderCapNormals(geo, height)

    const midPoint = (from + to) / 2
    const normalizedDepth = midPoint / depth

    const tangent = curve?.getTangentAt(normalizedDepth)!
    const axis = new Vector3(0, 1, 0)
    const quaternion = new Quaternion().setFromUnitVectors(axis.normalize(), tangent.normalize())
    const rotationMatrix = new Matrix4()
    rotationMatrix.makeRotationFromQuaternion(quaternion)
    geo.applyMatrix4(rotationMatrix)

    const position = curve?.getPointAt(normalizedDepth)
    if (!position) return
    geo.translate(position.x, position.y, position.z)
    return geo
  }

  _createAssayBatchedMesh(count: number, maxFromTo: number): BatchedMesh {
    const maxInstanceCount = count * maxFromTo
    const vertexCountPerGeometry = 34 // based on the assay cylinder geometry's position count
    const maxVertexCount = vertexCountPerGeometry * maxInstanceCount
    const maxIndexCount = 64 * maxInstanceCount // increase the multiplier if we encounter index buffer overflow
    const shaderMaterial = new ShaderMaterial({ ...assayShader })
    shaderMaterial.uniforms.uLightPositions.value = uLightPositions
    return new BatchedMesh(maxInstanceCount, maxVertexCount, maxIndexCount, shaderMaterial)
  }

  async createAllDrillHoles(
    holes: Map<number, DrillHoles>,
    registeredLegends: Map<string | number, DrillLegendViewModel>,
    sceneConfigs?: DrillDataConfig[],
  ) {
    // TODO - make cleanup conditional on whether we need to fully recreate the batched meshes for the new scene VRFY-14333
    this.cleanUp()

    for (const [dataSetId, drillHoles] of holes) {
      if (!drillHoles.localization || !drillHoles.data.length) continue

      const localizationOffset = new Vector3(
        drillHoles.localization.easting,
        drillHoles.localization.elevation,
        -drillHoles.localization.northing,
      )

      const config = sceneConfigs?.find(config => config.data_set_id === dataSetId) ?? drillHoles.config[0]

      this._createAllCollars(dataSetId, drillHoles.data, localizationOffset, drillHoles.count, config)
      this._createAllSurveys(dataSetId, drillHoles.data, localizationOffset, drillHoles.count, config)

      const legend = this.getSelectedLegendForDataSet(dataSetId, registeredLegends)
      if (legend && legend.type === 'numerical_assay') {
        this._createAllNumericalAssays(
          dataSetId,
          drillHoles.data,
          (legend as NumericalLegend).original.name,
          config,
          drillHoles.numerical_from_to_max_length,
          drillHoles.count,
        )
      } else if (legend && legend.type === 'categorical_assay') {
        this._createAllCategoricalAssays(dataSetId, drillHoles.data, config, drillHoles.categorical_from_to_max_length, drillHoles.count)
      }

      while (drillHoles.next) {
        const appendPage = true
        const nextDrillHoles = await this._getNextHoles(drillHoles.next)
        drillHoles.next = nextDrillHoles.next ?? null
        if (nextDrillHoles.results?.length) {
          drillHoles.data = drillHoles.data.concat(nextDrillHoles.results)
          this._createAllCollars(dataSetId, nextDrillHoles.results, localizationOffset, drillHoles.count, config, appendPage)
          this._createAllSurveys(dataSetId, nextDrillHoles.results, localizationOffset, drillHoles.count, config, appendPage)

          if (!legend) continue
          if (legend.type === 'numerical_assay') {
            this._createAllNumericalAssays(
              dataSetId,
              nextDrillHoles.results,
              (legend as NumericalLegend).original.name,
              config,
              drillHoles.numerical_from_to_max_length,
              drillHoles.count,
              appendPage,
            )
          } else if (legend.type === 'categorical_assay') {
            this._createAllCategoricalAssays(
              dataSetId,
              nextDrillHoles.results,
              config,
              drillHoles.categorical_from_to_max_length,
              drillHoles.count,
              appendPage,
            )
          }
        }
      }
    }
  }

  private async _getNextHoles(nextUrl: string): Promise<ApiListView<DrillHoleApiData>> {
    const drillHoles = await $fetch<ApiListView<DrillHoleApiData>>(nextUrl, {
      ...useFetchSharedOptions(),
      headers: {
        ...useFetchSharedOptions().headers,
        'X-Deck-Auth-UUID': `${this.loaderConfig.uuid}`,
      },
    })
    return drillHoles
  }

  setActiveAssay(
    dataSetId: number,
    holes: DrillHoles,
    registeredLegends: Map<string | number, DrillLegendViewModel>,
    sceneConfigs: DrillDataConfig[],
  ) {
    const assayMesh = this.assayMap.get(dataSetId)
    if (assayMesh) {
      this.group.remove(assayMesh)
      disposeScene(assayMesh)
    }
    const legend = this.getSelectedLegendForDataSet(dataSetId, registeredLegends)
    const config = sceneConfigs.find(config => config.data_set_id === dataSetId) ?? holes.config[0]

    if (!legend) return
    if (legend.type === 'numerical_assay') {
      this._createAllNumericalAssays(
        dataSetId,
        holes.data,
        (legend as NumericalLegend).original.name,
        config,
        holes.numerical_from_to_max_length,
        holes.count,
      )
    } else if (legend.type === 'categorical_assay') {
      this._createAllCategoricalAssays(dataSetId, holes.data, config, holes.categorical_from_to_max_length, holes.count)
    }
  }

  private _getGradeIndex(value: number, grades: Array<any>): number {
    const gradeIndex = grades.findIndex(grade => value <= grade.max && value >= grade.min)
    return gradeIndex
  }

  setCollarRadius(dataSetId: number, radius: number) {
    const collarMesh = this.collarMap.get(dataSetId)
    if (!collarMesh) return
    ;(collarMesh.material as ShaderMaterial).uniforms.uRadius.value = radius
  }

  setSurveyRadius(dataSetId: number, radius: number) {
    const surveyMesh = this.surveyMap.get(dataSetId)
    if (!surveyMesh) return
    ;(surveyMesh.material as ShaderMaterial).uniforms.uRadius.value = radius
  }

  setAssayRadius(dataSetId: number, radius: number) {
    const assayMesh = this.assayMap.get(dataSetId)
    if (!assayMesh) return
    ;(assayMesh.material as ShaderMaterial).uniforms.uRadius.value = radius
  }

  setCollarVisibility(dataSetId: number, visible: boolean) {
    const collarMesh = this.collarMap.get(dataSetId)
    if (!collarMesh) return
    collarMesh.visible = visible
  }

  setSurveyVisibility(dataSetId: number, visible: boolean) {
    const surveyMesh = this.surveyMap.get(dataSetId)
    if (!surveyMesh) return
    surveyMesh.visible = visible
  }

  setAssayVisibility(dataSetId: number, visible: boolean) {
    const assayMesh = this.assayMap.get(dataSetId)
    if (!assayMesh) return
    assayMesh.visible = visible
  }

  setAssayColor(dataSetId: number | undefined, payload: { id: string; idx: number; newColor: string }) {
    if (!dataSetId) return
    const assayMesh = this.assayMap.get(dataSetId)
    if (!assayMesh) return
    ;(assayMesh.material as ShaderMaterial).uniforms.uColors.value[payload.idx] = new Color(payload.newColor)
  }

  cleanUp() {
    this.assayMap.clear()
    this.group.clear()
    disposeScene(this.group)
  }
}
